import React from "react";
import { Shield, TrendingUp, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BENEFITS = [
  {
    icon: Shield,
    title: "Protect your sender reputation",
    description:
      "Reduced hard bounces, fewer spam complaints, stable IP/domain health.",
    features: [
      "Lower bounce rates",
      "Maintain sender score",
      "Avoid blacklisting",
      "Preserve domain reputation",
    ],
    color: "emerald",
    gradient: "from-emerald-500 to-teal-600",
  },
  {
    icon: TrendingUp,
    title: "Make every campaign count",
    description:
      "Higher inbox placement, more clicks, and revenue per send.",
    features: [
      "Boost open rates",
      "Increase conversions",
      "Maximize ROI",
      "Improve engagement",
    ],
    color: "sky",
    gradient: "from-sky-500 to-blue-600",
  },
  {
    icon: Lock,
    title: "Respect privacy and compliance",
    description:
      "Data minimization, short retention, and DPDP-inclusive practices.",
    features: [
      "GDPR compliant",
      "CCPA ready",
      "India DPDP aligned",
      "Secure by design",
    ],
    color: "violet",
    gradient: "from-violet-500 to-purple-600",
  },
];

const colorClasses = {
  emerald: {
    bg: "bg-emerald-50 dark:bg-emerald-900/10",
    border: "border-emerald-200 dark:border-emerald-800",
    text: "text-emerald-600 dark:text-emerald-400",
    dotBg: "bg-emerald-500",
  },
  sky: {
    bg: "bg-sky-50 dark:bg-sky-900/10",
    border: "border-sky-200 dark:border-sky-800",
    text: "text-sky-600 dark:text-sky-400",
    dotBg: "bg-sky-500",
  },
  violet: {
    bg: "bg-violet-50 dark:bg-violet-900/10",
    border: "border-violet-200 dark:border-violet-800",
    text: "text-violet-600 dark:text-violet-400",
    dotBg: "bg-violet-500",
  },
};

export default function CoreBenefitsSection() {
    const navigate = useNavigate();
    const handleTalk = () => {
        navigate("/product/email-validation");
    }
        const handleSales = () => {
        navigate("/company/contact");
    }
  return (
    <section className="py-16 lg:py-24 bg-slate-50 dark:bg-slate-900/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-slate-50 mb-4">
            More than validation.{" "}
            <span className="text-sky-600 dark:text-sky-400">
              Better deliverability, lower costs.
            </span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            VALIDMAIL doesn't just verify emails—it helps you build a
            sustainable, high-performing email strategy
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {BENEFITS.map((benefit, index) => {
            const Icon = benefit.icon;
            const colors = colorClasses[benefit.color];

            return (
              <div
                key={index}
                className="group relative bg-white dark:bg-slate-900 rounded-2xl p-8 border-2 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
              >
                <div
                  className={`absolute inset-0 ${colors.bg} opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-300`}
                />

                <div className="relative">
                  <div
                    className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${benefit.gradient} mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                  >
                    <Icon className="h-8 w-8 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                    {benefit.title}
                  </h3>

                  <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                    {benefit.description}
                  </p>

                  <ul className="space-y-3">
                    {benefit.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-3">
                        <div
                          className={`w-2 h-2 rounded-full ${colors.dotBg} group-hover:scale-125 transition-transform`}
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="absolute top-4 right-4 w-24 h-24 bg-gradient-to-br from-transparent to-slate-100/50 dark:to-slate-800/50 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            );
          })}
        </div>

        <div className="mt-16 bg-gradient-to-r from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-2xl p-8 md:p-12 border border-sky-200 dark:border-sky-800">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-4">
              Ready to improve your email deliverability?
            </h3>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              Join thousands of teams who trust VALIDMAIL to keep their lists
              clean and their campaigns performing at their best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
              onClick={handleTalk}
              className="px-8 py-4 rounded-full text-base font-semibold bg-sky-600 text-white hover:bg-sky-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 transform duration-200">
                Start validating emails
              </button>
              <button
              onClick={handleSales}
              className="px-8 py-4 rounded-full text-base font-semibold border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-800 transition-all">
                Talk to sales
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
